import React from 'react';
import {
  Checkbox,
  Divider,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent
} from '@mui/material';
import { capitalize } from 'lodash-es';

interface ContentTypeSelectProps {
  formControlColor: 'primary' | 'secondary';
  classes: Record<string, string>;
  value: string[] | [];
  setValue: (arg0: string, arg1: string[]) => void;
  contentTypes: string[];
  contentTypesAllowed: string[];
  t: (key: string) => string;
  contentSearchFiltersTestIds: string;
}

const contentTypeOrganizer = {
  select_All: 'all',
  clear_All: 'all_clear'
};

const getContentTypeValue = (value: string[], contentTypes: string[], contentTypesAllowed: string[]): string[] => {
  if (value.includes(contentTypeOrganizer.clear_All) || value.length === 0) {
    return [];
  }

  if (!value || !contentTypes.length) {
    const [firstContentTypeAllowed] = contentTypesAllowed || [];
    return firstContentTypeAllowed ? [firstContentTypeAllowed.toLowerCase()] : [];
  }

  return value;
};

const ContentTypeSelect: React.FC<ContentTypeSelectProps> = ({
  formControlColor,
  classes,
  value,
  setValue,
  contentTypes,
  contentTypesAllowed,
  t,
  contentSearchFiltersTestIds
}) => {
  // Ensure value is always an array
  const safeValue = Array.isArray(value) ? value : [];
  const currentValue = getContentTypeValue(safeValue, contentTypes, contentTypesAllowed);

  const handleChange = (evt: SelectChangeEvent<string[]>) => {
    const selected = evt.target.value as string[];
    if (selected.length === 0 || selected.includes(contentTypeOrganizer.select_All)) {
      setValue('contentType', contentTypes);
    } else if (selected.includes(contentTypeOrganizer.clear_All)) {
      setValue('contentType', []);
    } else {
      setValue('contentType', selected);
    }
  };

  return (
    <FormControl color={formControlColor} className={classes.contentTypeFilter}>
      <InputLabel id="filter" htmlFor="content-type">
        {t('layouts.content_type')}
      </InputLabel>

      <Select
        id="content-type"
        multiple
        label={t('layouts.content_type')}
        data-testid={contentSearchFiltersTestIds}
        labelId="filter"
        value={currentValue}
        onChange={handleChange}
        renderValue={(selected) => {
          // Ensure selected is always an array
          const selectedArray = Array.isArray(selected) ? selected : [];
          return selectedArray.map((s) => capitalize(s.replace(/_/g, ' '))).join(', ');
        }}
      >
        <MenuItem value={contentTypeOrganizer.select_All}>
          <ListItemText primary={t('collections.content_type.all')} />
        </MenuItem>
        <MenuItem value={contentTypeOrganizer.clear_All}>
          <ListItemText primary={t('collections.content_type.all_clear')} />
        </MenuItem>
        <Divider />
        {contentTypes.map((type) => (
          <MenuItem key={type} value={type}>
            <Checkbox
              checked={currentValue.includes(type)}
              sx={{
                color: 'grey',
                '&.Mui-checked': {
                  color: 'grey'
                }
              }}
            />
            <ListItemText primary={capitalize(type.replace(/_/g, ' '))} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default ContentTypeSelect;
